import { Download, Lock } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { approveWindow, bytes, decryptSegment, unwrapWindowKey } from "@tether/crypto";
import { paths } from "@tether/shared";
import { useSession } from "../App.js";
import { api, apiBytes } from "../lib/api.js";
import { loadGroupKey, openSealed } from "../lib/keys.js";
import { Spinner } from "../lib/Spinner.js";
import { touchVault } from "../lib/recentVault.js";

interface Grant {
  vaultId: string;
  vaultPublicKey: string;
  windows: {
    deviceId: string;
    windowId: string;
    epoch: string;
    key: string;
    segments: string[];
    partials: { index: number; value: string }[];
  }[];
}
interface Decrypted {
  name: string;
  streamId: string;
  seq: number;
  size: number;
  url: string;
  mime: string;
}

const MIME: Record<string, string> = { audio: "audio/mp4", video: "video/mp4" };

function isoLocal(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * Kid mode's one-click "decrypt now": chains request → self-approve → grant → decrypt into a single
 * action. There is no one else to approve — the requester is always the vault's sole shareholder in
 * kid mode — so showing that as three separate steps just reads as a broken flow.
 */
export function KidDecrypt() {
  const { vaultId = "" } = useParams();
  const { me } = useSession();
  const [from, setFrom] = useState(isoLocal(new Date(Date.now() - 3600_000)));
  const [to, setTo] = useState(isoLocal(new Date()));
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [files, setFiles] = useState<Decrypted[]>([]);

  const vault = me?.vaults.find((v) => v.id === vaultId);

  async function decryptNow() {
    if (!me || !vault || vault.mode !== "kid" || !vault.sealedShareForOwner) return;
    setBusy(true);
    setErr(null);
    setFiles([]);
    try {
      const fromMs = new Date(from).getTime();
      const toMs = new Date(to).getTime();
      const created = await api<{ request: { id: string; windows: Grant["windows"] } }>(
        `/v1/vaults/${vaultId}/unlock-requests`,
        { body: { fromMs, toMs, reason: "other" } },
      );
      const id = created.request.id;

      const kp = loadGroupKey(me.user.id);
      if (!kp)
        throw new Error(
          "Your decryption key is missing in this browser. Restore it from your backup in Settings.",
        );
      const shareBytes = openSealed(kp, vault.sealedShareForOwner, `share:${vault.publicKey}:1`);
      const share = { index: 1, value: shareBytes };
      const partials: string[] = [];
      const windows = created.request.windows;
      for (let i = 0; i < windows.length; i++) {
        setProgress(`Preparing ${i + 1} of ${windows.length}…`);
        const wrapped = await apiBytes(
          `/v1/vaults/${vaultId}/object?key=${encodeURIComponent(windows[i]!.key)}`,
        );
        partials.push(bytes.toBase64Url(approveWindow(share, wrapped).value));
      }
      shareBytes.fill(0);
      await api(`/v1/unlock-requests/${id}/approve`, { body: { partials } });

      const g = await api<Grant>(`/v1/unlock-requests/${id}/grant`);
      const vpk = bytes.fromBase64Url(g.vaultPublicKey);
      const out: Decrypted[] = [];
      let n = 0;
      const total = g.windows.reduce((a, w) => a + w.segments.length, 0);
      for (const w of g.windows) {
        const wrapped = await apiBytes(
          `/v1/vaults/${g.vaultId}/object?key=${encodeURIComponent(w.key)}`,
        );
        const ref = paths.windowRef(w.windowId, w.epoch);
        const wPartials = w.partials.map((p) => ({
          index: p.index,
          value: bytes.fromBase64Url(p.value),
        }));
        const wk = unwrapWindowKey(vpk, g.vaultId, ref, wrapped, wPartials);
        for (const segKey of w.segments) {
          n++;
          setProgress(`Decrypting ${n} of ${total}…`);
          const m = /\/([a-z0-9]+)-(\d+)\.seg$/.exec(segKey);
          if (!m) continue;
          const streamId = m[1]!;
          const seq = Number(m[2]);
          const ct = await apiBytes(
            `/v1/vaults/${g.vaultId}/object?key=${encodeURIComponent(segKey)}`,
          );
          const pt = decryptSegment(wk, g.vaultId, ref, streamId, seq, ct);
          // Android's MediaRecorder stamps AAC output with the `3gp4` major brand even when asked
          // for MPEG-4 output; Chrome's <audio> rejects that brand/MIME mismatch. Bit-identical
          // audio, just relabeled.
          if (
            streamId === "audio" &&
            pt.length >= 12 &&
            pt[4] === 0x66 &&
            pt[5] === 0x74 &&
            pt[6] === 0x79 &&
            pt[7] === 0x70 &&
            pt[8] === 0x33 &&
            pt[9] === 0x67 &&
            pt[10] === 0x70 &&
            pt[11] === 0x34
          ) {
            pt[8] = 0x6d;
            pt[9] = 0x70;
            pt[10] = 0x34;
            pt[11] = 0x32;
          }
          const mime = MIME[streamId] ?? "application/octet-stream";
          const url = URL.createObjectURL(new Blob([pt as BlobPart], { type: mime }));
          out.push({
            name: `${w.deviceId}-${w.windowId}-${w.epoch}-${streamId}-${seq}`,
            streamId,
            seq,
            size: pt.length,
            url,
            mime,
          });
        }
        wk.fill(0);
      }
      out.sort((a, b) => a.streamId.localeCompare(b.streamId) || a.seq - b.seq);
      setFiles(out);
      touchVault(me.user.id, vaultId);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }

  if (!vault) return <Spinner label="Loading…" />;
  if (vault.mode !== "kid") {
    return (
      <div className="card">
        <p className="error">This is only for kid-mode vaults.</p>
        <Link to={`/vaults/${vaultId}`}>
          <button>Back to vault</button>
        </Link>
      </div>
    );
  }

  return (
    <div>
      <h2 style={{ margin: "0 0 12px", display: "flex", alignItems: "center", gap: 8 }}>
        <Lock size={20} className="icon" />
        {vault.name} · decrypt recordings
      </h2>
      <div className="card">
        <p className="muted">
          Pick a time range and decrypt — this device holds the only key needed, so there's nothing
          to wait on. Decryption happens in this browser; the gateway never sees the key.
        </p>
        <div className="row">
          <div>
            <label>From</label>
            <input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <label>To</label>
            <input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
        </div>
        {progress && <p>{progress}</p>}
        {err && <p className="error">{err}</p>}
        <div className="row" style={{ marginTop: 12 }}>
          <button
            className="primary"
            onClick={decryptNow}
            disabled={busy}
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            <Lock size={15} />
            {busy ? "Decrypting…" : "Decrypt"}
          </button>
        </div>
      </div>

      {files.length > 0 && (
        <div className="card">
          <table>
            <thead>
              <tr>
                <th>Segment</th>
                <th>Size</th>
                <th>Play</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {files.map((f) => (
                <tr key={f.name}>
                  <td>{f.name}</td>
                  <td>{(f.size / 1024).toFixed(1)} KB</td>
                  <td>
                    {f.streamId === "audio" && <audio controls src={f.url} preload="none" />}
                    {f.streamId === "video" && (
                      <video controls src={f.url} width={220} preload="none" />
                    )}
                  </td>
                  <td>
                    <a
                      href={f.url}
                      download={`${f.name}.${f.streamId === "audio" ? "m4a" : f.streamId === "video" ? "mp4" : "bin"}`}
                      style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                    >
                      <Download size={14} />
                      Download
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
